// Runtime 3D asset pack.
//
// Loads `models/wr/manifest.json` (written by `scripts/gltf-to-md3`) and the MD3
// models it names, uploads them to the renderer, and exposes `key -> GPU model
// id` + per-key metadata (scale, orientation offsets, bob/spin/align flags).
//
// The manifest, parsed meshes and decoded textures are cached across map loads;
// only the GPU upload is redone after `renderer.clearModels()` (see
// `resetAssetGpu` / `loadAssetPack` in `main.ts`). If there is no manifest (no
// art converted yet) every lookup misses and the caller falls back to the
// procedural placeholders — nothing breaks.

import type { Renderer } from "./render/renderer";
import { parseMd3 } from "./render/md3";
import type { Md3Model } from "./render/md3";
import { loadTexture } from "./render/textures";
import type { DecodedImage } from "./render/textures";
import { api } from "./base";

export interface AssetEntry {
  /** Model path relative to the pack root, e.g. `models/wr/weapons/rocket/view.md3`. */
  model: string;
  surfaces?: string[];
  textures?: string[];
  /** Uniform scale applied at render time (converter bakes unit scale, so usually 1). */
  scale?: number;
  /** Local placement offset, in the instance's rotated frame (world units). */
  originOffset?: [number, number, number];
  /** Extra yaw (degrees) added to the instance's own yaw. */
  yawOffset?: number;
  pitchOffset?: number;
  /** Viewmodel-only placement: `[forward, right, up, pitch, roll, yaw]`. */
  handOffset?: [number, number, number, number, number, number];
  animFps?: number;
  tris?: number;
  budgetTris?: number;
  /** Model-space muzzle/barrel-tip point (engine units), for VFX anchoring. */
  muzzle?: [number, number, number];
  bounds?: { min: [number, number, number]; max: [number, number, number] };
  category?: string;
  /** World pickups: bob/spin like the placeholder. */
  bob?: boolean;
  spin?: boolean;
  /** Projectiles: orient the model along its velocity. */
  alignVelocity?: boolean;
  viewmodel?: boolean;
}

export interface AssetManifest {
  version: number;
  style?: string;
  units?: string;
  /** Content hash of the asset table (busts the browser cache on a new pack). */
  hash?: string;
  assets: Record<string, AssetEntry>;
}

const MANIFEST_PATH = "models/wr/manifest.json";
const IMG_EXT = /\.(tga|jpg|jpeg|png|webp)$/i;

let manifest: AssetManifest | null = null;
let manifestPromise: Promise<AssetManifest | null> | null = null;
/** Cache-buster for model/texture URLs, from the manifest. */
let packHash = "";

/** Parsed meshes, cached across map loads (keyed by model path). */
const modelCache = new Map<string, Md3Model>();
/** Decoded textures, cached across map loads (keyed by extension-less path). */
const texCache = new Map<string, DecodedImage | null>();
/** GPU model ids for the current map (cleared on every map load). */
const gpu = new Map<string, number>();

/** Whether the pack manifest has been fetched (regardless of success). */
export function packReady(): boolean {
  return manifest !== null;
}

/** Metadata for a key, or null if the key isn't in the manifest. */
export function assetEntry(key: string): AssetEntry | null {
  return manifest?.assets?.[key] ?? null;
}

/** GPU model id for a key, or -1 (not loaded / not present → use placeholder). */
export function assetModelId(key: string): number {
  return gpu.get(key) ?? -1;
}

/** How many assets are uploaded for the current map. */
export function assetGpuCount(): number {
  return gpu.size;
}

/** Keys present in the (cached) manifest. */
export function assetKeys(): string[] {
  return manifest ? Object.keys(manifest.assets) : [];
}

async function fetchManifest(): Promise<AssetManifest | null> {
  if (manifest) return manifest;
  if (!manifestPromise) {
    manifestPromise = (async () => {
      try {
        // `no-store` forces a network fetch even if a previous provider cached
        // the manifest with a long max-age — otherwise a new pack never shows up.
        const res = await fetch(api("/model/" + MANIFEST_PATH), { cache: "no-store" });
        if (!res.ok) return null;
        const json = (await res.json()) as AssetManifest;
        if (!json?.assets) return null;
        manifest = json;
        packHash = json.hash ?? "";
        return manifest;
      } catch {
        return null;
      }
    })();
  }
  return manifestPromise;
}

function modelUrl(path: string): string {
  const url = api("/model/" + path.split("/").filter(Boolean).map(encodeURIComponent).join("/"));
  return packHash ? `${url}?v=${packHash}` : url;
}

/** Parse (cached) + upload one model, resolving its per-surface textures. */
async function loadModel(renderer: Renderer, path: string): Promise<number> {
  let md3 = modelCache.get(path);
  if (!md3) {
    const res = await fetch(modelUrl(path));
    if (!res.ok) throw new Error(`model ${path}: ${res.status}`);
    md3 = parseMd3(await res.arrayBuffer());
    modelCache.set(path, md3);
  }

  // Resolve every distinct shader name (extension stripped, like spawnModel).
  const names = new Set<string>();
  for (const s of md3.surfaces) for (const n of s.shaderNames) if (n) names.add(n);
  await Promise.all(
    [...names].map(async (n) => {
      const key = n.replace(IMG_EXT, "");
      if (!texCache.has(key)) texCache.set(key, await loadTexture(key, packHash));
    }),
  );

  const texIds: (number | null)[] = md3.surfaces.map((s) => {
    const raw = s.shaderNames[0];
    if (!raw) return null;
    const img = texCache.get(raw.replace(IMG_EXT, "")) ?? null;
    return img ? renderer.registerTexture(img) : null;
  });
  return renderer.uploadModel(md3, texIds);
}

/**
 * Ensure the manifest is loaded and (re)upload the named keys (all by default).
 * Safe to call after every map load; already-uploaded keys are skipped and
 * missing files are warned about and skipped.
 * @param keys optional subset (e.g. just the current weapon's viewmodel).
 */
export async function loadAssetPack(renderer: Renderer, keys?: string[]): Promise<number> {
  const man = await fetchManifest();
  if (!man) return 0;
  const wanted = (keys ?? Object.keys(man.assets)).filter((k) => man.assets[k]?.model && !gpu.has(k));

  let loaded = 0;
  const worker = async () => {
    for (;;) {
      const key = wanted.shift();
      if (!key) return;
      try {
        gpu.set(key, await loadModel(renderer, man.assets[key].model));
        loaded++;
      } catch (e) {
        console.warn("asset load failed:", key, e);
      }
    }
  };
  // A few concurrent loaders to avoid a serial request waterfall.
  await Promise.all(Array.from({ length: 4 }, worker));
  return loaded;
}

/** Forget GPU ids — call right after `renderer.clearModels()` on map load. */
export function resetAssetGpu(): void {
  gpu.clear();
}
